// Left-pane structure tree — flat plan-list layout (source-map
// restructure MR 5b).
//
// Prior MR 4 introduced dense-row cards; MR 5b flattens them to match
// the designer's mock (source-editable.html):
//   - No card border on rows — just border-bottom separators + a 3px
//     border-left that toggles between transparent (default), brand
//     (current row), and warn (edited row).
//   - Chapter is a single compact .chaprow at the top of the plan list
//     (surface2 background, non-editable title, chapter-scope token
//     badge). No pattern disclosure, no action cluster on the chapter.
//   - Action cluster (Split · Merge up · Rename · Delete) is absolutely
//     positioned top-right of the sub-section row; revealed on hover
//     or when the row is the current one. Subtler than MR 4's version
//     (no per-button borders, mono kbd suffix inline).
//   - Whole row is clickable — click → focus (drives current section
//     on the right pane). Nested affordances (heading contenteditable,
//     pattern disclosure toggle, action cluster buttons) stop click
//     propagation so they don't accidentally re-focus.
//
// Reading model change (MR 5b): the right pane now renders only ONE
// section at a time (see SourceDocPane), so clicking a row on the
// left explicitly sets what the reviewer reads. No scroll-spy loop.
// MR 11 will add J / K keyboard nav to the same setFocused writer.
//
// Preserved from MR 4:
//   - Per-row EDITED / NEW / EDITING tags driven by baselineChapter
//   - PatternDisclosure component (collapsed by default)
//   - NoMatchPill + ExcludedPill
//   - Delete = hard-remove (soft-delete-with-hatched-fill still on
//     MR 8's desk pending a `deleted?: boolean` decision on the wire)

import { useRef, useState } from 'react';
import type { ResolvedChapter, ResolvedSubSection } from '@gc-coach/shared';
import { classifyChunkSize } from '@gc-coach/shared';
import { ChunkSizeBadge } from './ChunkSizeBadge.js';
import styles from './MapTree.module.css';

export interface MapTreeFocus {
  /** 'chapter' = the chapter row is focused (right pane defaults to
   *  the first sub for reading). Number = a sub-section index. */
  target: 'chapter' | number;
}

export interface MapTreeProps {
  chapter: ResolvedChapter;
  /** Last-saved shape of the same chapter — powers per-row
   *  EDITED / NEW tags. Optional; unset renders no tags. */
  baselineChapter?: ResolvedChapter;
  chapterNumber: number;
  totalChapters: number;
  focused: MapTreeFocus | null;
  readOnly: boolean;
  onChange: (next: ResolvedChapter) => void;
  onFocus: (focus: MapTreeFocus | null) => void;
  onPrevChapter: () => void;
  onNextChapter: () => void;
  t: (k: string) => string;
}

export function MapTree(p: MapTreeProps) {
  return (
    <div className={styles.tree}>
      {/* Chapter pager — prev/next + `CHAPTER n / N`. The
        * "N CHAPTERS REVIEWED" sub-line the mock shows is deferred
        * until the wire has a per-chapter reviewed flag. */}
      <ChapterPager
        chapterNumber={p.chapterNumber}
        totalChapters={p.totalChapters}
        onPrev={p.onPrevChapter}
        onNext={p.onNextChapter}
        t={p.t}
      />

      <div className={styles.planList}>
        {/* Chapter row — compact, non-editable label + token badge.
          * Clicking it focuses the chapter (right pane defaults to
          * the first sub for reading in this state). */}
        <ChapterRow
          chapter={p.chapter}
          {...(p.baselineChapter?.estimated_tokens !== undefined
            ? { wasEstimatedTokens: p.baselineChapter.estimated_tokens }
            : {})}
          isFocused={p.focused?.target === 'chapter'}
          onFocus={() => p.onFocus({ target: 'chapter' })}
          t={p.t}
        />

        {/* Sub-section rows. */}
        {p.chapter.sub_sections.map((sub, subIdx) => {
          const baselineSub = p.baselineChapter?.sub_sections[subIdx];
          const isNew = p.baselineChapter != null && baselineSub == null;
          const isEdited = baselineSub != null && isSubEdited(sub, baselineSub);
          const isFocused = p.focused?.target === subIdx;
          const wasTokens =
            baselineSub && typeof baselineSub.estimated_tokens === 'number'
              ? baselineSub.estimated_tokens
              : undefined;
          return (
            <SubSectionRow
              key={subIdx}
              sub={sub}
              index={subIdx}
              isFirst={subIdx === 0}
              isNew={isNew}
              isEdited={isEdited}
              isFocused={isFocused}
              {...(wasTokens !== undefined ? { wasEstimatedTokens: wasTokens } : {})}
              readOnly={p.readOnly}
              onChange={(next) => {
                p.onChange({
                  ...p.chapter,
                  sub_sections: p.chapter.sub_sections.map((s, i) =>
                    i === subIdx ? next : s,
                  ),
                });
              }}
              onFocus={() => p.onFocus({ target: subIdx })}
              t={p.t}
            />
          );
        })}

        {!p.readOnly && p.chapter.char_start !== null && (
          <button
            type="button"
            className={styles.addSubSectionButton}
            onClick={() => {
              p.onChange({
                ...p.chapter,
                sub_sections: [...p.chapter.sub_sections, blankSubSection()],
              });
            }}
          >
            + {p.t('mapReview.treeAddSubSection')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Chapter pager ───────────────────────────────────────────────────

interface ChapterPagerProps {
  chapterNumber: number;
  totalChapters: number;
  onPrev: () => void;
  onNext: () => void;
  t: (k: string) => string;
}

function ChapterPager(p: ChapterPagerProps) {
  const canPrev = p.chapterNumber > 1;
  const canNext = p.chapterNumber < p.totalChapters;
  return (
    <div className={styles.pager}>
      <button
        type="button"
        className={styles.pagerButton}
        onClick={p.onPrev}
        disabled={!canPrev}
        aria-label={p.t('mapReview.treePrevChapter')}
      >
        ‹
      </button>
      <div className={styles.pagerLabel}>
        {p.t('mapReview.metaChapterLabel')} {p.chapterNumber} /{' '}
        {p.totalChapters}
      </div>
      <button
        type="button"
        className={styles.pagerButton}
        onClick={p.onNext}
        disabled={!canNext}
        aria-label={p.t('mapReview.treeNextChapter')}
      >
        ›
      </button>
    </div>
  );
}

// ─── Chapter row (compact, non-editable) ─────────────────────────────

interface ChapterRowProps {
  chapter: ResolvedChapter;
  /** Chapter's last-saved token count. Powers ChunkSizeBadge's
   *  `↓ was ~N` delta chip on the chapter roll-up. */
  wasEstimatedTokens?: number;
  isFocused: boolean;
  onFocus: () => void;
  t: (k: string) => string;
}

function ChapterRow(p: ChapterRowProps) {
  const showBadge = p.chapter.estimated_tokens != null;
  return (
    <button
      type="button"
      className={joinClasses(
        styles.chaprow,
        p.isFocused && styles.chaprowFocused,
      )}
      onClick={p.onFocus}
    >
      <span className={styles.chaprowTitle}>{p.chapter.title}</span>
      {showBadge && (
        <ChunkSizeBadge
          estimatedTokens={p.chapter.estimated_tokens!}
          isChunk={p.chapter.sub_sections.length === 0}
          {...(p.wasEstimatedTokens !== undefined
            ? { wasEstimatedTokens: p.wasEstimatedTokens }
            : {})}
          t={p.t}
        />
      )}
    </button>
  );
}

// ─── Sub-section row ─────────────────────────────────────────────────

interface SubSectionRowProps {
  sub: ResolvedSubSection;
  index: number;
  /** First row — no "Merge up" action (there's nothing above to merge into). */
  isFirst: boolean;
  isNew: boolean;
  isEdited: boolean;
  isFocused: boolean;
  /** Last-saved token count for this sub (matched by index against
   *  baselineChapter). Feeds ChunkSizeBadge's `↓ was ~N` delta chip. */
  wasEstimatedTokens?: number;
  readOnly: boolean;
  onChange: (next: ResolvedSubSection) => void;
  onFocus: () => void;
  t: (k: string) => string;
}

function SubSectionRow(p: SubSectionRowProps) {
  const [isEditingHeading, setIsEditingHeading] = useState(false);
  const headingRef = useRef<HTMLInputElement>(null);
  const noMatch = p.sub.char_start === null;
  const excluded = p.sub.excluded === true;
  const showEditedRule = p.isEdited && !p.isFocused;

  // Tier-tinted focused background (Pierre's UX suggestion — diverges
  // from the mock's solid --brand-soft). The token badge already carries
  // tier info; using the same colour on the row background reinforces
  // the actionable signal ("current section AND it's amber") without
  // needing to look twice. Brand left rule stays as the "you are here"
  // marker so both signals are legible together. Tiny + null tiers fall
  // back to --brand-soft since there's no strong tier signal to show.
  const tier = classifyChunkSize(p.sub.estimated_tokens ?? 0);
  const focusedTierClass =
    p.isFocused && tier === 'good'
      ? styles.srFocusedGood
      : p.isFocused && tier === 'large'
        ? styles.srFocusedLarge
        : p.isFocused && tier === 'oversized'
          ? styles.srFocusedOversized
          : null;

  const rowClass = joinClasses(
    styles.sr,
    noMatch && styles.srNoMatch,
    excluded && styles.srExcluded,
    showEditedRule && styles.srEdited,
    p.isFocused && styles.srFocused,
    focusedTierClass,
  );

  // Row-scope click → focus. Nested interactive elements
  // (headingInput, pattern disclosure, action cluster buttons) each
  // stop propagation so the row doesn't re-focus on their clicks —
  // that separation is what keeps "inspect pattern" from moving the
  // right pane (the bug MR 4/5 shipped).
  return (
    <div
      className={rowClass}
      role="button"
      tabIndex={0}
      onClick={p.onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          p.onFocus();
        }
      }}
    >
      {/* Row-cluster actions moved to the right-pane SectionFloatingMenu
        * on 2026-10-21 — reading + editing happen on the same pane now,
        * no need to pane-switch for Split / Merge up / Delete. Rename
        * stays implicit: click the heading text (which already is an
        * input styled as plain text) and start typing. */}

      {/* Row 1 — index label + edit tag + status pills */}
      <div className={styles.sr1}>
        <span className={styles.srIx}>
          {p.t('mapReview.treeSubSectionPrefixUpper')} {p.index + 1}
        </span>
        {p.isNew && <EditTag kind="new" t={p.t} />}
        {!p.isNew && p.isEdited && <EditTag kind="edited" t={p.t} />}
        {isEditingHeading && <EditTag kind="editing" t={p.t} />}
        {noMatch && <NoMatchPill t={p.t} />}
        {excluded && (
          <span
            className={styles.excludedPill}
            title={p.t('mapReview.treeExcludedTooltip')}
          >
            {p.t('mapReview.treeExcludedPill')}
          </span>
        )}
      </div>

      {/* Row 2 — heading. Styled to look like plain text until focused. */}
      <input
        ref={headingRef}
        className={styles.srt}
        value={p.sub.label}
        onChange={(e) => p.onChange({ ...p.sub, label: e.target.value })}
        onFocus={() => setIsEditingHeading(true)}
        onBlur={() => setIsEditingHeading(false)}
        onClick={(e) => e.stopPropagation()}
        disabled={p.readOnly}
        placeholder={p.t('mapReview.treeSubSectionLabelPlaceholder')}
        aria-label={p.t('mapReview.treeSubSectionLabelPlaceholder')}
      />

      {/* Row 3 — token badge + collapsed pattern disclosure. */}
      <div className={styles.sr2}>
        {p.sub.estimated_tokens != null && !excluded && (
          <ChunkSizeBadge
            estimatedTokens={p.sub.estimated_tokens}
            isChunk={true}
            {...(p.wasEstimatedTokens !== undefined
              ? { wasEstimatedTokens: p.wasEstimatedTokens }
              : {})}
            t={p.t}
          />
        )}
        <PatternDisclosure
          pattern={p.sub.header_pattern}
          onChange={(next) => p.onChange({ ...p.sub, header_pattern: next })}
          readOnly={p.readOnly}
          t={p.t}
        />
      </div>
    </div>
  );
}

// ─── Pattern disclosure (unchanged) ──────────────────────────────────

interface PatternDisclosureProps {
  pattern: string;
  onChange: (next: string) => void;
  readOnly: boolean;
  t: (k: string) => string;
}

function PatternDisclosure(p: PatternDisclosureProps) {
  const [expanded, setExpanded] = useState(false);
  // Only the input itself stops propagation — the wrapper used to, but
  // that was `flex:1` so it ate all of Row 3's empty space and
  // silently blocked the row's own onClick (which drives current
  // section on the right pane). Clicking the disclosure toggle now
  // both toggles AND focuses the row; clicking to type into the
  // expanded pattern input still stops propagation so typing doesn't
  // re-fire focus.
  return (
    <div className={styles.mp}>
      <button
        type="button"
        className={styles.mpToggle}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded ? 'true' : 'false'}
      >
        {expanded ? '▾' : '▸'} {p.t('mapReview.treePatternDisclosureLabel')}
      </button>
      {expanded && (
        <div className={styles.mpBody}>
          <input
            className={styles.mpInput}
            value={p.pattern}
            onChange={(e) => p.onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            disabled={p.readOnly}
            placeholder={p.t('mapReview.treePatternPlaceholder')}
            spellCheck={false}
          />
          <p className={styles.mpHint}>{p.t('mapReview.treePatternHint')}</p>
        </div>
      )}
    </div>
  );
}

// ─── Edit tag ────────────────────────────────────────────────────────

function EditTag({
  kind,
  t,
}: {
  kind: 'edited' | 'new' | 'editing';
  t: (k: string) => string;
}) {
  const cls =
    kind === 'edited'
      ? styles.tagEdited
      : kind === 'new'
        ? styles.tagNew
        : styles.tagEditing;
  const labelKey =
    kind === 'edited'
      ? 'mapReview.editTagEdited'
      : kind === 'new'
        ? 'mapReview.editTagNew'
        : 'mapReview.editTagEditing';
  return <span className={cls}>{t(labelKey)}</span>;
}

// ─── No-match pill ───────────────────────────────────────────────────

function NoMatchPill({ t }: { t: (k: string) => string }) {
  return (
    <span
      className={styles.noMatchPill}
      title={t('mapReview.treeNoMatchTooltip')}
    >
      ⚠ {t('mapReview.treeNoMatchPill')}
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function blankSubSection(): ResolvedSubSection {
  return {
    label: '',
    header_pattern: '',
    char_start: null,
    char_end: null,
  };
}

function joinClasses(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

function isSubEdited(
  current: ResolvedSubSection,
  baseline: ResolvedSubSection,
): boolean {
  return (
    current.label !== baseline.label ||
    current.header_pattern !== baseline.header_pattern ||
    (current.excluded ?? false) !== (baseline.excluded ?? false)
  );
}
